import React, { useEffect, useMemo, useState } from "react";
import { CommonInput, CommonModal, CustomSelect } from "components";
import { Images } from "constants";
import { updateUserProfile, uploadFile } from "_services/userService";
import { FaPencil } from "react-icons/fa6";
import toast from "react-hot-toast";

const EditUserProfile = ({
  userId,
  userIdToUpdate,
  userData = {},
  props: modalProps,
}) => {
  const [formData, setFormData] = useState({
    firstName: userData.firstName || "",
    lastName: userData.lastName || "",
    email: userData.email || "",
    userProfileImageUrl: userData.userProfileImageUrl || "",
    birdId: userData.birdId?._id || "",
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [formError, setFormError] = useState({});
  const [birdStatusList, setBirdStatusList] = useState([]);

  const getMasterData = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("masterData"));
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (userData) {
      setFormData({
        firstName: userData.firstName || "",
        lastName: userData.lastName || "",
        email: userData.email || "",
        userProfileImageUrl: userData.userProfileImageUrl || "",
        birdId: userData.birdId?._id || "",
      });
    }
  }, [userData]);

  useEffect(() => {
    const birds = getMasterData?.birds;

    if (Array.isArray(birds) && birds.length) {
      const birdList = birds.map((i) => ({
        label: i?.title,
        value: i?._id,
      }));
      setBirdStatusList([...birdList]);
    }
  }, [getMasterData]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validate = () => {
    const errors = {};
    if (!formData.firstName.trim()) errors.firstName = "First name is required";
    if (!formData.lastName.trim()) errors.lastName = "Last name is required";
    if (!formData.email.trim()) errors.email = "Email is required";
    return errors;
  };

  const handleFileUpload = async (e) => {
    try {
      const file = e.target.files[0];
      if (!file) return;
      const response = await uploadFile(file);
      if (response?.data?.file?.fullUrl) {
        setFormData((prev) => ({
          ...prev,
          userProfileImageUrl: response?.data?.file?.fullUrl,
        }));
      }
    } catch (err) {
      console.error("Upload failed:", err);
    }
  };

  const handleSubmit = async () => {
    const errors = validate();
    if (Object.keys(errors).length) {
      setFormError(errors);
      return;
    }

    setLoading(true);
    setFormError({});
    setMessage("");

    try {
      const payload = {
        ...formData,
        userId,
        userIdToUpdate,
      };

      const response = await updateUserProfile(payload);
      modalProps?.closeModal();

      const successMessage = response?.message || "User updated successfully!";
      toast.success(successMessage);

      if (modalProps.onSuccess) {
        modalProps.onSuccess();
      }
    } catch (err) {
      toast.error(err);
    } finally {
      setLoading(false);
    }
  };

  const modalConfig = {
    ...modalProps,
    onSubmit: handleSubmit,
    submitLoading: loading,
    submitTitle: "Update User",
  };

  return (
    <CommonModal {...modalConfig}>
      <div className="addLr_body">
        <div className="_allDflx flex flex-col gap-4">
          <div className="__profilPic_bx ">
            <img
              src={formData.userProfileImageUrl || Images.USERLIMG}
              alt="User Profile"
              className=""
            />
            <button
              type="button"
              className="__profilPic_edit "
              onClick={() => document.getElementById("fileInput").click()}
            >
              <FaPencil />
            </button>
            <input
              style={{ display: "none" }}
              type="file"
              id="fileInput"
              accept="image/*"
              className="hidden"
              onChange={handleFileUpload}
            />
          </div>

          <CommonInput
            type="text"
            label="First Name *"
            placeholder="Enter First Name"
            name="firstName"
            value={formData.firstName}
            onChange={({ target: { name, value } }) =>
              handleChange(name, value)
            }
            focus={!!formError.firstName}
            error={formError.firstName}
          />

          <CommonInput
            type="text"
            label="Last Name *"
            placeholder="Enter Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={({ target: { name, value } }) =>
              handleChange(name, value)
            }
            focus={!!formError.lastName}
            error={formError.lastName}
          />

          <CommonInput
            type="email"
            label="Email *"
            placeholder="Enter Email"
            name="email"
            value={formData.email}
            onChange={({ target: { name, value } }) =>
              handleChange(name, value)
            }
            focus={!!formError.email}
            error={formError.email}
          />

          <CustomSelect
            options={birdStatusList}
            value={birdStatusList.find((i) => i.value === formData?.birdId)}
            name="birdId"
            label="Select Bird"
            placeholder="Select Bird"
            onSelect={(data) => handleChange("birdId", data?.value)}
            focus={!!formError.birdId}
            error={formError.birdId}
          />

          {message && <p className="text-center text-green-600">{message}</p>}
        </div>
      </div>
    </CommonModal>
  );
};

export default EditUserProfile;
